import { useEffect, useRef, useState, type ReactNode } from 'react';

interface LazyOnVisibleProps {
  children: ReactNode;
  className?: string;
  minHeight?: number | string;
  rootMargin?: string;
  onVisible?: () => void;
}

export default function LazyOnVisible({
  children,
  minHeight = 320,
  rootMargin = '400px 0px',
  className,
  onVisible,
}: LazyOnVisibleProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (visible) return;

    if (typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      onVisible?.();
      return;
    }

    const el = ref.current;
    if (!el) return;

    const obs = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setVisible(true);
            onVisible?.();
            obs.disconnect();
            break;
          }
        }
      },
      { rootMargin },
    );

    obs.observe(el);
    return () => obs.disconnect();
  }, [onVisible, rootMargin, visible]);

  return (
    <div
      ref={ref}
      className={className}
      style={{ minHeight }}
    >
      {visible ? children : null}
    </div>
  );
}
